import { useCallback } from 'react';

import { useMutation } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

/**
 * Best-effort client-side throttle window.
 */
const WARM_UP_THROTTLE_MS = 60_000;
const NEW_THREAD_KEY = '__new-thread__';
const lastWarmedAt = new Map<string, number>();

export const resetNewThreadWarmThrottle = () => lastWarmedAt.delete(NEW_THREAD_KEY);

/**
 * Warms the Kim AgentCore runtime so the user's next message doesn't pay the cold-start
 * cost. When a thread already exists (in-thread composer) we warm that exact session;
 * otherwise we warm a per-user session as a fallback. Returns a silent, fire-and-forget
 * callback intended to be wired to the Kim input's focus event.
 */
export const useWarmKim = (matterId?: string, subsetId?: string, threadId?: string) => {
  const { getFetchConfig } = useGetFetchConfig();

  const { mutate } = useMutation<void, Error>(async () => {
    const fetchConfig = getFetchConfig({ method: FetchMethod.POST });
    const query = threadId ? `?threadId=${threadId}` : '';
    const response = await fetch(
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/warmup${query}`,
      fetchConfig,
    );
    if (!response.ok) {
      throw new Error('Kim warm-up failed');
    }
  });

  return useCallback(() => {
    if (!matterId || !subsetId) return;

    const throttleKey = threadId ?? NEW_THREAD_KEY;
    const now = Date.now();
    if (now - (lastWarmedAt.get(throttleKey) ?? 0) < WARM_UP_THROTTLE_MS) return;

    // Open the window optimistically so a rapid re-focus doesn't double-fire while the
    // request is in flight, then roll it back if the warm-up fails so the next focus can
    // retry rather than being suppressed by a failed attempt.
    const previous = lastWarmedAt.get(throttleKey);
    lastWarmedAt.set(throttleKey, now);

    mutate(undefined, {
      onError: () => {
        // Only roll back if nothing newer has since claimed the window.
        if (lastWarmedAt.get(throttleKey) !== now) return;
        if (previous === undefined) {
          lastWarmedAt.delete(throttleKey);
        } else {
          lastWarmedAt.set(throttleKey, previous);
        }
      },
    });
  }, [matterId, subsetId, threadId, mutate]);
};
